import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { useCreateServiceOption } from "@/hooks/useServices";
import { TargetAudience, PricingModel, DiscountType, ClaimLimit, ClaimFrequency } from "@/types/services";
import type { CreateServiceOptionRequest } from "@/types/services";

interface CreateServiceOptionModalProps {
  open: boolean;
  onClose: () => void;
  serviceId: string;
  serviceName?: string;
}

export default function CreateServiceOptionModal({ open, onClose, serviceId, serviceName }: CreateServiceOptionModalProps) {
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<CreateServiceOptionRequest>({
    defaultValues: {
      isActive: true,
      sortOrder: 0,
      targetAudience: TargetAudience.Individual,
      pricingModel: PricingModel.Fixed,
      currency: "AED"
    }
  });

  const createServiceOption = useCreateServiceOption();

  const targetAudience = watch("targetAudience");
  const pricingModel = watch("pricingModel");
  const discountType = watch("discountType");
  const claimLimit = watch("claimLimit");
  const claimFrequency = watch("claimFrequency");
  const isActive = watch("isActive");

  const onSubmit = async (data: CreateServiceOptionRequest) => {
    try {
      await createServiceOption.mutateAsync({ serviceId, data });
      toast({
        title: "Service option created",
        description: "Service option has been created successfully.",
      });
      reset();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create service option. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Service Option</DialogTitle>
          <DialogDescription>
            Add a new option to the service "{serviceName}".
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Option Name *</Label>
                <Input
                  id="name"
                  {...register("name", { required: "Option name is required" })}
                  placeholder="Enter option name"
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sortOrder">Sort Order</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  min="0"
                  {...register("sortOrder", { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Enter option description"
                rows={3}
              />
            </div>
          </div>

          {/* Target & Pricing */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Target & Pricing</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="targetAudience">Target Audience *</Label>
                <Select
                  value={targetAudience}
                  onValueChange={(value) => setValue("targetAudience", value as TargetAudience)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select target audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TargetAudience.Individual}>Individual</SelectItem>
                    <SelectItem value={TargetAudience.Company}>Company</SelectItem>
                    <SelectItem value={TargetAudience.Both}>Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pricingModel">Pricing Model *</Label>
                <Select
                  value={pricingModel}
                  onValueChange={(value) => setValue("pricingModel", value as PricingModel)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select pricing model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PricingModel.Fixed}>Fixed</SelectItem>
                    <SelectItem value={PricingModel.PerPerson}>Per Person</SelectItem>
                    <SelectItem value={PricingModel.Discount}>Discount</SelectItem>
                    <SelectItem value={PricingModel.Benefit}>Benefit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="basePrice">Base Price</Label>
                <Input
                  id="basePrice"
                  type="number"
                  min="0"
                  step="0.01"
                  {...register("basePrice", { valueAsNumber: true })}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Input
                  id="currency"
                  {...register("currency")}
                  placeholder="AED"
                  maxLength={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  {...register("duration", { valueAsNumber: true })}
                  placeholder="60"
                />
              </div>
            </div>
          </div>

          {/* Capacity */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Capacity</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="minCapacity">Minimum Capacity</Label>
                <Input
                  id="minCapacity"
                  type="number"
                  min="1"
                  {...register("minCapacity", { valueAsNumber: true })}
                  placeholder="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxCapacity">Maximum Capacity</Label>
                <Input
                  id="maxCapacity"
                  type="number"
                  min="1"
                  {...register("maxCapacity", { valueAsNumber: true })}
                  placeholder="100"
                />
              </div>
            </div>
          </div>

          {/* Discount */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Discount</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discountType">Discount Type</Label>
                <Select
                  value={discountType || ""}
                  onValueChange={(value) => setValue("discountType", value as DiscountType)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select discount type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DiscountType.Percentage}>Percentage</SelectItem>
                    <SelectItem value={DiscountType.FixedAmount}>Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="discountValue">Discount Value</Label>
                <Input
                  id="discountValue"
                  type="number"
                  min="0"
                  step="0.01"
                  {...register("discountValue", { valueAsNumber: true })}
                  placeholder="0"
                  disabled={!discountType}
                />
              </div>
            </div>
          </div>

          {/* Claims */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Claim Settings</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="claimLimit">Claim Limit</Label>
                <Select
                  value={claimLimit || ""}
                  onValueChange={(value) => setValue("claimLimit", value as ClaimLimit)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select claim limit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ClaimLimit.PerEmployee}>Per Employee</SelectItem>
                    <SelectItem value={ClaimLimit.PerCompany}>Per Company</SelectItem>
                    <SelectItem value={ClaimLimit.Unlimited}>Unlimited</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="claimFrequency">Claim Frequency</Label>
                <Select
                  value={claimFrequency || ""}
                  onValueChange={(value) => setValue("claimFrequency", value as ClaimFrequency)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select claim frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ClaimFrequency.Once}>Once</SelectItem>
                    <SelectItem value={ClaimFrequency.Daily}>Daily</SelectItem>
                    <SelectItem value={ClaimFrequency.Monthly}>Monthly</SelectItem>
                    <SelectItem value={ClaimFrequency.Yearly}>Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Additional Information</h3>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Enter any additional notes"
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) => setValue("isActive", checked)}
              />
              <Label htmlFor="isActive" className="text-sm font-medium">
                Active
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createServiceOption.isPending}>
              {createServiceOption.isPending ? "Creating..." : "Create Option"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}